import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { setPageMeta, breadcrumbLd } from '../utils/seo';
import { getTrade, HIRE_TRADES } from '../data/hireTrades';
import { HIRE_CITIES } from '../data/hireCities';
import { CheckCircle, CaretDown, UsersThree, Briefcase, CurrencyDollar, ArrowRight } from '@phosphor-icons/react';
import AdBanner from '../components/AdBanner';
import { getArticlesForTrade } from '../data/internalLinks';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const an = (w) => (/^[aeiou]/i.test(w) ? 'an' : 'a');

const Faq = ({ f, i }) => {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="border-2 border-neutral-900 dark:border-neutral-700 bg-white dark:bg-neutral-800">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 p-4 text-left" data-testid={`hire-faq-${i}`}>
        <span className="font-black text-neutral-900 dark:text-white">{f.q}</span>
        <CaretDown size={18} weight="bold" className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="px-4 pb-4 text-neutral-600 dark:text-neutral-300 text-sm leading-relaxed">{f.a}</p>}
    </div>
  );
};

export default function HireTradePage() {
  const { slug } = useParams();
  const trade = getTrade(slug);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!trade) return;
    window.scrollTo(0, 0);
    setStats(null);
    axios.get(`${API_URL}/api/trade-stats`, { params: { terms: trade.searchTerms } })
      .then((r) => setStats(r.data)).catch(() => {});
    setPageMeta({
      title: trade.metaTitle,
      description: trade.metaDescription,
      url: `${window.location.origin}/hire/${trade.slug}`,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: trade.faqs.map((f) => ({
            '@type': 'Question', name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
        breadcrumbLd([
          { name: 'Home', url: window.location.origin },
          { name: 'Hire a Pro', url: `${window.location.origin}/hire` },
          { name: trade.name },
        ]),
      ],
    });
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!trade) {
    return (
      <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] flex items-center justify-center">
        <div className="text-center">
          <p className="font-black text-xl text-neutral-900 dark:text-white mb-4">Trade not found</p>
          <Link to="/hire" className="text-[#D95800] font-bold underline">Browse all trades</Link>
        </div>
      </div>
    );
  }

  const related = trade.related.map(getTrade).filter(Boolean);

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]" data-testid="hire-trade-page">
      {/* Hero */}
      <div className="bg-neutral-900 border-b-4 border-[#D95800]">
        <div className="max-w-5xl mx-auto px-4 py-12 grid lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-3">
            <nav className="text-xs text-neutral-400 font-bold mb-4">
              <Link to="/" className="hover:text-white">Home</Link> / <Link to="/hire" className="hover:text-white" data-testid="hire-breadcrumb">Hire a Pro</Link> / <span className="text-white">{trade.name}</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl font-black text-white uppercase leading-tight mb-4">{trade.h1}</h1>
            <p className="text-neutral-300 max-w-3xl leading-relaxed">{trade.intro}</p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/get-quotes" className="h-12 px-6 flex items-center gap-2 bg-[#D95800] hover:bg-[#E55D00] text-white font-black uppercase border-2 border-white shadow-[3px_3px_0px_rgba(255,255,255,0.3)]" data-testid="hire-get-quotes-btn">
                Get 3 Free Quotes <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/find-workers" className="h-12 px-6 flex items-center gap-2 bg-transparent text-white font-black uppercase border-2 border-white hover:bg-white/10" data-testid="hire-browse-pros-btn">
                Browse {trade.name}
              </Link>
            </div>
          </div>
          <div className="hidden lg:block lg:col-span-2">
            <img src={`/hire/${trade.slug}.jpg`} alt={`${trade.trade} pro working on a residential project`}
              className="w-full aspect-[4/3] object-cover border-2 border-white shadow-[6px_6px_0px_#D95800]"
              loading="eager" data-testid="hire-trade-photo" />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10 space-y-10">
        {/* Live stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="hire-stats">
          <div className="bg-white dark:bg-neutral-800 border-2 border-neutral-900 dark:border-neutral-700 p-4 shadow-[3px_3px_0px_#000]">
            <UsersThree size={20} weight="bold" className="text-[#D95800]" />
            <p className="text-xl font-black mt-1 dark:text-white">{stats ? (stats.pros >= 5 ? stats.pros : stats.total_pros) : '—'}</p>
            <p className="text-xs font-bold uppercase text-neutral-500">Verified pros on ConstructHelp</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-neutral-900 dark:border-neutral-700 p-4 shadow-[3px_3px_0px_#000]">
            <Briefcase size={20} weight="bold" className="text-[#D95800]" />
            <p className="text-xl font-black mt-1 dark:text-white">{stats ? stats.open_jobs : '—'}</p>
            <p className="text-xs font-bold uppercase text-neutral-500">Open {trade.trade.toLowerCase()} jobs right now</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-neutral-900 dark:border-neutral-700 p-4 shadow-[3px_3px_0px_#000]">
            <CurrencyDollar size={20} weight="bold" className="text-[#D95800]" />
            <p className="text-sm font-black mt-1 dark:text-white leading-snug">{trade.avgCost}</p>
            <p className="text-xs font-bold uppercase text-neutral-500">Typical 2026 pricing</p>
          </div>
        </div>

        {/* Sponsored banner */}
        <AdBanner placement="hire" trade={trade.slug} />

        {/* Hiring tips */}
        <div>
          <h2 className="text-lg font-black uppercase text-neutral-900 dark:text-white mb-4">Before you hire {an(trade.singular)} {trade.singular}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {trade.tips.map((t, i) => (
              <div key={i} className="flex items-start gap-3 bg-white dark:bg-neutral-800 border-2 border-neutral-900 dark:border-neutral-700 p-4">
                <CheckCircle size={20} weight="fill" className="text-[#D95800] shrink-0 mt-0.5" />
                <p className="text-sm text-neutral-700 dark:text-neutral-300">{t}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Cost guide cross-link */}
        {trade.costGuideSlug && (
          <Link to={`/guides/${trade.costGuideSlug}`}
            className="block bg-neutral-900 border-2 border-neutral-900 p-5 shadow-[4px_4px_0px_#D95800] hover:translate-x-0.5 transition-transform"
            data-testid="hire-cost-guide-link">
            <p className="text-xs font-black uppercase text-[#D95800] mb-1">2026 Cost Guide</p>
            <p className="font-black text-white">How much does {trade.trade.toLowerCase()} work cost? See the full price breakdown →</p>
          </Link>
        )}

        {/* Features cross-link */}
        <Link to="/home" data-testid="hire-features-strip"
          className="block bg-white border-2 border-black p-4 hover:bg-orange-50 shadow-[3px_3px_0px_#000]">
          <p className="text-xs font-black uppercase text-[#D95800] mb-0.5">Free tools for pros & homeowners</p>
          <p className="font-black text-sm text-neutral-900">Voice translator, AI scheduling, escrow payments & more — see everything you get →</p>
        </Link>

        {/* Related hiring guides */}
        {getArticlesForTrade(trade.slug).length > 0 && (
          <div data-testid="hire-related-articles">
            <h2 className="text-sm font-black uppercase text-neutral-500 mb-3">Smart hiring reads</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {getArticlesForTrade(trade.slug).map((g) => (
                <Link key={g.slug} to={`/hiring-guides/${g.slug}`}
                  className="bg-white dark:bg-neutral-800 border-2 border-neutral-900 dark:border-neutral-700 p-4 hover:bg-orange-50 shadow-[3px_3px_0px_#000]"
                  data-testid={`hire-article-${g.slug}`}>
                  <p className="font-black text-sm text-neutral-900 dark:text-white leading-snug mb-1">{g.h1}</p>
                  <span className="text-xs font-bold text-[#D95800]">Read the guide →</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* FAQs */}
        <div>
          <h2 className="text-lg font-black uppercase text-neutral-900 dark:text-white mb-4">Frequently asked questions</h2>
          <div className="space-y-3">
            {trade.faqs.map((f, i) => <Faq key={i} f={f} i={i} />)}
          </div>
        </div>

        {/* CTA band */}
        <div className="bg-[#D95800] border-2 border-neutral-900 p-8 text-center shadow-[5px_5px_0px_#000]">
          <h2 className="text-2xl font-black uppercase text-white mb-2">Ready to hire {an(trade.singular)} {trade.singular}?</h2>
          <p className="text-orange-100 mb-5">Post your project once — verified local pros send you free quotes. Pay safely with milestone escrow.</p>
          <Link to="/get-quotes" className="inline-flex h-12 px-8 items-center bg-neutral-900 text-white font-black uppercase border-2 border-white" data-testid="hire-cta-bottom">
            Get Free Quotes
          </Link>
        </div>

        {/* City pages for this trade */}
        <div>
          <h2 className="text-sm font-black uppercase text-neutral-500 mb-3">{trade.name} by city</h2>
          <div className="flex flex-wrap gap-2" data-testid="trade-city-links">
            {HIRE_CITIES.map((c) => (
              <Link key={c.slug} to={`/hire/${trade.slug}/${c.slug}`}
                className="px-3 py-2 bg-white dark:bg-neutral-800 border-2 border-neutral-900 dark:border-neutral-700 text-sm font-bold text-neutral-800 dark:text-neutral-200 hover:bg-orange-50">
                {c.name}, {c.abbr}
              </Link>
            ))}
          </div>
        </div>

        {/* Related trades */}
        <div>
          <h2 className="text-sm font-black uppercase text-neutral-500 mb-3">Homeowners also hire</h2>
          <div className="flex flex-wrap gap-2">
            {related.map((r) => (
              <Link key={r.slug} to={`/hire/${r.slug}`}
                className="px-3 py-2 bg-white dark:bg-neutral-800 border-2 border-neutral-900 dark:border-neutral-700 text-sm font-bold text-neutral-800 dark:text-neutral-200 hover:bg-orange-50"
                data-testid={`related-trade-${r.slug}`}>
                {r.name}
              </Link>
            ))}
            <Link to="/hire" className="px-3 py-2 text-sm font-bold text-[#D95800] underline">All trades →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
